import {
  MeshStandardMaterial,
  MeshPhysicalMaterial,
  Color,
  DoubleSide,
  FrontSide,
  type ColorRepresentation,
  type Material,
} from 'three';
import type { PaletteConfig, TextureSelections } from '@/model/schema';
import { loadTextureSet, setTextureRepeatForRect, type TextureSet } from './textureLibrary';
import { TEXTURE_SLOT_DIR } from '@/generator/texturesGenerator';

/**
 * A material kit is allocated per generated house so we can cheaply dispose
 * all materials when the house is replaced. Do NOT mutate these in-place
 * during render — that breaks the "canonical config is the source of truth"
 * invariant (Pitfall 7 in the design doc).
 */
export interface MaterialKit {
  basePlate: MeshStandardMaterial;
  /** Lot outdoor ground (lawn / grass). Separate from `basePlate` so the
   *  lot can wear a grass texture while interior slabs, decks, balconies
   *  keep the solid palette colour. */
  outdoorFloor: MeshStandardMaterial;
  /** Interior floor slab (laminate / wood / tile). Split from `basePlate`
   *  because interior and exterior slabs now wear different materials —
   *  laminate inside, plain concrete outside. */
  interiorFloor: MeshStandardMaterial;
  exteriorWall: MeshStandardMaterial;
  /** Flat-roof slab. Single-sided so it doesn't z-fight with the wall top cap. */
  roof: MeshStandardMaterial;
  /** Open-prism roof slopes (gable / hip). DoubleSide so the eaves' underside
   *  is visible from below when peeled open. */
  roofSlope: MeshStandardMaterial;
  trim: MeshStandardMaterial;
  fence: MeshStandardMaterial;
  mailbox: MeshStandardMaterial;
  interior: MeshStandardMaterial;
  window: MeshPhysicalMaterial;
  /** Solid window frame — opaque trim around the pane. */
  windowFrame: MeshStandardMaterial;
  door: MeshStandardMaterial;
  doorFrame: MeshStandardMaterial;
  doorKnob: MeshStandardMaterial;
  /** Interior door slab (in the partition openings the planner picked). */
  interiorDoor: MeshStandardMaterial;
  /** Stair tread / riser material. */
  stair: MeshStandardMaterial;
  /** Basement floor — distinct concrete look so the well reads as
   *  separate from the lot ground above. */
  basement: MeshStandardMaterial;
  /** Basement wall — same family as `basement` but slightly lighter. */
  basementWall: MeshStandardMaterial;
  /** Driveway — paved approach to a garage, distinct from grass / gravel
   *  ground (lot base plate). */
  driveway: MeshStandardMaterial;
  dispose(): void;
}

/**
 * Solid opaque surfaces (walls, roof, floor slabs, base plate, fence, etc.)
 * use FrontSide so that back-face culling suppresses the downward-facing
 * top cap / upward-facing bottom cap of adjacent stories. With DoubleSide
 * the two coincident caps at every story seam z-fight, producing the
 * flickering horizontal band that was visible above the base plate and
 * between stories.
 */
function solid(color: ColorRepresentation, roughness = 0.85, metalness = 0): MeshStandardMaterial {
  const m = new MeshStandardMaterial({ color: new Color(color), roughness, metalness });
  m.side = FrontSide;
  return m;
}

/** Roof slope material — DoubleSide so the slope's underside reads from
 *  below in peel views. Used for the gable/hip BufferGeometry slabs. The
 *  flat roof keeps a separate FrontSide material to avoid z-fighting with
 *  the top story's wall top cap. */
function roofSlope(color: ColorRepresentation, roughness = 0.6): MeshStandardMaterial {
  const m = new MeshStandardMaterial({ color: new Color(color), roughness });
  m.side = DoubleSide;
  return m;
}

/** Real glass — physical material with light transmission and a faint tint.
 *  Renders as actual translucent glazing instead of an opaque blue rectangle. */
function glass(color: ColorRepresentation): MeshPhysicalMaterial {
  const m = new MeshPhysicalMaterial({
    color: new Color(color),
    roughness: 0.05,
    metalness: 0,
    transmission: 0.9,
    thickness: 0.05,
    ior: 1.5,
    transparent: true,
    opacity: 0.45,
    clearcoat: 0.4,
    clearcoatRoughness: 0.1,
  });
  m.side = DoubleSide;
  return m;
}

export function createMaterialKit(
  palette: PaletteConfig,
  textures?: TextureSelections,
): MaterialKit {
  const kit: MaterialKit = {
    basePlate: solid(palette.basePlate, 0.95),
    // Green lawn default so it still reads as grass when the grass
    // texture hasn't loaded yet (or is intentionally disabled).
    outdoorFloor: solid('#5a7a3a', 0.95),
    // Warm neutral so untextured interior slabs still read as floor
    // (not matching the basePlate concrete tone used for decks etc.).
    interiorFloor: solid('#b7a584', 0.7),
    exteriorWall: solid(palette.exteriorWall, 0.85),
    roof: solid(palette.roof, 0.6),
    roofSlope: roofSlope(palette.roof, 0.6),
    trim: solid(palette.trim, 0.5),
    fence: solid(palette.fence, 0.75),
    mailbox: solid(palette.mailbox, 0.5),
    interior: solid(palette.interior, 0.9),
    window: glass(palette.window),
    windowFrame: solid(palette.trim, 0.6),
    door: solid(palette.door, 0.55),
    doorFrame: solid(palette.trim, 0.6),
    doorKnob: solid('#c2a14a', 0.35, 0.7),
    interiorDoor: solid(palette.door, 0.55),
    stair: solid(palette.trim, 0.7),
    basement: solid('#5a5854', 0.95),
    basementWall: solid('#6e6c68', 0.9),
    driveway: solid('#7a7a78', 0.9),
    dispose() {
      for (const [key, value] of Object.entries(this)) {
        if (key === 'dispose') continue;
        (value as Material).dispose?.();
      }
    },
  };

  // Apply optional texture overrides. Texture loading is async; we
  // attach maps as soon as they resolve, then bump `needsUpdate` so
  // Three.js rebuilds the shader. The flat color stays as a tint that
  // the shader multiplies against the albedo (white-ish base recommended
  // when a texture provides full color).
  //
  // Each slot passes a representative `rect` that the texture library
  // turns into a UV repeat. Because several meshes share a single
  // material, the repeat is computed once per material for the rect
  // that best fits the majority of its target meshes; strips that
  // deviate from that size show slightly denser/sparser tiling but the
  // texture identity is preserved.
  if (textures?.exteriorWall) {
    // Exterior wall meshes use `bakeFrameWorldUVs` (see `uv.ts`) to
    // encode facade-local / world-local coordinates (in meters) into
    // their UVs. With that convention, the right texture repeat is
    // simply `(1 / tileMeters, 1 / tileMeters)` so one texture tile
    // spans `tileMeters` of wall no matter how the wall is broken up
    // into pillars, sills, lintels, jambs, or terrace interior walls
    // (user-reported: textures used to scale differently on every
    // subsection — see the cut-out brick wall screenshot).
    void applyTextureToMaterial(
      kit.exteriorWall,
      slotDir('exteriorWall'),
      textures.exteriorWall,
      { widthMeters: 1, heightMeters: 1 },
    );
  }
  if (textures?.outdoorFloor) {
    // Typical lot is ~20 × 20 m. Using the lot extent means the grass
    // tiles at the texture's real-world size (`tileMeters`) across the
    // whole lot instead of being stretched or repeating only twice.
    void applyTextureToMaterial(
      kit.outdoorFloor,
      slotDir('outdoorFloor'),
      textures.outdoorFloor,
      { widthMeters: 20, heightMeters: 20 },
    );
  }
  if (textures?.pavement) {
    // Driveway ~3 × 8 m is the dominant paved mesh; walkway legs are
    // narrower but share the material — minor density variation on the
    // walkway is acceptable versus splitting materials just for UVs.
    void applyTextureToMaterial(
      kit.driveway,
      slotDir('pavement'),
      textures.pavement,
      { widthMeters: 3, heightMeters: 8 },
    );
  }
  if (textures?.interiorFloor) {
    // Interior slab strips are commonly 3-6 m across. Use 4 m as the
    // representative rect so typical rooms show several laminate tiles
    // without the planks being stretched to the point of looking faked.
    void applyTextureToMaterial(
      kit.interiorFloor,
      slotDir('interiorFloor'),
      textures.interiorFloor,
      { widthMeters: 4, heightMeters: 4 },
    );
  }

  return kit;
}

/** Resolve a schema slot name to its texture-directory name. Kept tiny
 *  here so callers don't have to import the mapping directly and so we
 *  fail loud if a slot ever misses a directory entry. */
function slotDir(slot: keyof TextureSelections): string {
  const dir = TEXTURE_SLOT_DIR[slot];
  if (!dir) throw new Error(`No texture directory mapped for slot "${slot}"`);
  return dir;
}

interface TextureRect {
  widthMeters: number;
  heightMeters: number;
}

async function applyTextureToMaterial(
  material: MeshStandardMaterial,
  category: string,
  id: string,
  rect: TextureRect,
): Promise<void> {
  try {
    const set = await loadTextureSet(category, id);
    bindTextureSet(material, set, rect);
  } catch (err) {
    console.warn(`[textures] failed to load ${category}/${id}`, err);
  }
}

function bindTextureSet(
  material: MeshStandardMaterial,
  set: TextureSet,
  rect: TextureRect,
): void {
  const m = set.meta;
  // Drive UV repeat from real-world tile size so textures look right
  // across varying mesh sizes without per-mesh UV rewrites.
  setTextureRepeatForRect(set, rect.widthMeters, rect.heightMeters);
  if (set.albedo) {
    material.map = set.albedo;
    // Albedo provides the colour; tint with white so the texture isn't
    // double-multiplied by the palette colour.
    material.color = new Color('#ffffff');
  }
  if (set.normal) {
    material.normalMap = set.normal;
    material.normalScale.set(m.normalScale ?? 1, m.normalScale ?? 1);
  }
  if (set.roughness) {
    material.roughnessMap = set.roughness;
  }
  if (set.ao) {
    material.aoMap = set.ao;
    material.aoMapIntensity = m.aoIntensity ?? 1;
  }
  // NOTE: displacement maps are intentionally NOT applied here. Our wall
  // meshes are plain `BoxGeometry`s which only have 4 vertices per face,
  // so a displacement map only moves the FACE CORNERS. Adjacent facades
  // would then be pulled apart along their respective outward normals at
  // each shared corner edge, opening up a visible gap at every house
  // corner (what was reported as "gap only with textures"). Real
  // displacement requires a tessellated mesh; until we ship that, we
  // fake surface relief purely via the normal map, which costs nothing
  // and never moves geometry.
  void set.displacement;
  void m.displacementScale;
  material.needsUpdate = true;
}

export const DEFAULT_PALETTES: Record<string, PaletteConfig> = {
  warmModern: {
    basePlate: '#3a4750',
    exteriorWall: '#e6dccb',
    roof: '#2b2b2b',
    trim: '#1f1f1f',
    fence: '#6d5d45',
    mailbox: '#a63a2a',
    interior: '#b7a584',
    window: '#9ec8e8',
    door: '#a63a2a',
  },
  coolSuburban: {
    basePlate: '#4a4e52',
    exteriorWall: '#c9d7dc',
    roof: '#4c3a2e',
    trim: '#2a2a2a',
    fence: '#3a4750',
    mailbox: '#2e5a88',
    interior: '#a7b2bd',
    window: '#aed1e8',
    door: '#2e5a88',
  },
  terracottaCompact: {
    basePlate: '#524135',
    exteriorWall: '#f0e2c8',
    roof: '#a75a3a',
    trim: '#3b2b20',
    fence: '#6b4a36',
    mailbox: '#3b2b20',
    interior: '#c9b491',
    window: '#c0dbe8',
    door: '#3b2b20',
  },
  moodyTall: {
    basePlate: '#2a2d31',
    exteriorWall: '#4f565c',
    roof: '#1a1a1a',
    trim: '#d9ba7a',
    fence: '#1f1f1f',
    mailbox: '#d9ba7a',
    interior: '#8a8f95',
    window: '#b8d3e5',
    door: '#d9ba7a',
  },
  pastelCourtyard: {
    basePlate: '#3e4a3a',
    exteriorWall: '#f3e5d8',
    roof: '#7a8f6c',
    trim: '#2e3a2a',
    fence: '#c8b892',
    mailbox: '#7a8f6c',
    interior: '#cdbfa6',
    window: '#c6def0',
    door: '#7a8f6c',
  },
};
